// ─── Roles ──────────────────────────────────────────────────────────────
export const USER_ROLES = [
  "super_admin",
  "campaign_owner",
  "content_producer",
  "ae",
  "people_ops",
  "read_only",
] as const;
export type UserRole = (typeof USER_ROLES)[number];

export const USER_STATUSES = ["active", "invited", "disabled"] as const;
export type UserStatus = (typeof USER_STATUSES)[number];

// ─── Status eligibility (board #2624) ───────────────────────────────────
// ONE definition of what each status may do, so viewer / survey / audience /
// admin logic cannot drift apart again (they had: tier.ts allowed invited
// while surveyAudience.ts required active — same rule, two answers).
//
// The rule:
//   viewer/audience materials → active OR invited. An invited teammate is a
//     real, pending recipient: onboarding packs target them, and their pack
//     links are viewer-tokenized so they need no admin login.
//   admin surfaces           → active ONLY. Invited must never gain admin
//     powers before accepting.
//   disabled                 → denied EVERYWHERE, at access time.
// Unknown/unmatched status falls through to false (fail closed).

/** Denied everywhere, always checked at access time — never by assuming the
 *  grant rows were cleaned up (#2624). */
export function isDisabledStatus(status: string | null | undefined): boolean {
  return status === "disabled";
}

/** May receive/open assigned viewer materials: active OR invited. */
export function isViewerEligibleStatus(status: string | null | undefined): boolean {
  return status === "active" || status === "invited";
}

/** May use admin surfaces / hold admin powers: active only. */
export function isAdminEligibleStatus(status: string | null | undefined): boolean {
  return status === "active";
}

// ─── Campaigns ──────────────────────────────────────────────────────────
export const CAMPAIGN_STATUSES = ["active", "paused", "archived"] as const;
export type CampaignStatus = (typeof CAMPAIGN_STATUSES)[number];

export const CONSENT_TIERS = [
  "anonymized_only",
  "named_with_nda",
  "fully_public",
] as const;
export type ConsentTier = (typeof CONSENT_TIERS)[number];

// ─── Videos ─────────────────────────────────────────────────────────────
export const VIDEO_CATEGORIES = [
  "corporate_intro",
  "campaign_case_study",
  "objection_handling",
  "performance_report",
  "role_specific",
] as const;
export type VideoCategory = (typeof VIDEO_CATEGORIES)[number];

export const ACCESS_TIERS = [
  "public",
  "registered",
  "nda_signed",
  "campaign_assigned",
] as const;
export type AccessTier = (typeof ACCESS_TIERS)[number];

export const ACCESS_TIER_LEVEL: Record<AccessTier, number> = {
  public: 0,
  registered: 1,
  nda_signed: 2,
  campaign_assigned: 3,
};

export const VIDEO_STATUSES = [
  "draft",
  "pending_approval",
  "approved",
  "rejected",
  "archived",
] as const;
export type VideoStatus = (typeof VIDEO_STATUSES)[number];

export const VIDEO_TAG_DIMENSIONS = [
  "audience",
  "role",
  "industry",
  "stage",
  "function",
] as const;
export type VideoTagDimension = (typeof VIDEO_TAG_DIMENSIONS)[number];

export const VIDEO_APPROVAL_ACTIONS = ["submitted", "approved", "rejected"] as const;
export type VideoApprovalAction = (typeof VIDEO_APPROVAL_ACTIONS)[number];

// Categories whose playlist entries pin to the version-at-send-time.
// Everything else follows the video's current version.
export const PIN_AT_SEND_CATEGORIES: ReadonlyArray<VideoCategory> = [
  "performance_report",
];

// ─── Playlists ──────────────────────────────────────────────────────────
export const PLAYLIST_TYPES = ["template", "custom"] as const;
export type PlaylistType = (typeof PLAYLIST_TYPES)[number];

export const PLAYLIST_MAX_VIDEOS = 15;

// ─── Viewers / Issuances ────────────────────────────────────────────────
export const INTEREST_RATINGS = [
  "very_interested",
  "interested",
  "not_interested",
] as const;
export type InterestRating = (typeof INTEREST_RATINGS)[number];

export const INTEREST_RATING_ORDER: Record<InterestRating, number> = {
  very_interested: 0,
  interested: 1,
  not_interested: 3,
};
export const UNRATED_ORDER = 2;

// ─── Campaign Assignments ───────────────────────────────────────────────
export const ASSIGNMENT_SUBJECT_TYPES = ["user", "viewer"] as const;
export type AssignmentSubjectType = (typeof ASSIGNMENT_SUBJECT_TYPES)[number];

// ─── Campaign member segments (CEO Rev 1.0) ─────────────────────────────
// Which audience a campaign_assignments grant (or a survey) represents. Drives
// the Overview "Audience Intelligence" cards and the Team-tab member roles.
// Campaign CEO is also derivable from campaigns.ownerUserId (single owner).
export const CAMPAIGN_MEMBER_SEGMENTS = [
  "campaign_ceo",
  "campaign_team",
  "specific_member",
  "existing_client",
  "lead",
  "investor",
] as const;
export type CampaignMemberSegment = (typeof CAMPAIGN_MEMBER_SEGMENTS)[number];

// ─── Surveys (CEO Rev 1.0 — replaces the never-built Knowledge Base) ─────
export const SURVEY_STATUSES = ["draft", "active", "closed"] as const;
export type SurveyStatus = (typeof SURVEY_STATUSES)[number];

export const SURVEY_QUESTION_TYPES = [
  "short_text",
  "long_text",
  "single_choice",
  "multi_choice",
  "rating",
  "boolean",
] as const;
export type SurveyQuestionType = (typeof SURVEY_QUESTION_TYPES)[number];

// ─── Deliverables (CEO Rev 1.0 — campaign-scoped work items) ─────────────
export const DELIVERABLE_STATUSES = [
  "not_started",
  "in_progress",
  "blocked",
  "delivered",
  "accepted",
] as const;
export type DeliverableStatus = (typeof DELIVERABLE_STATUSES)[number];

// ─── Events ─────────────────────────────────────────────────────────────
export const EVENT_TYPES = [
  "pack_sent",
  "viewer_opened",
  "identified",
  "nda_signed",
  "topic_rated",
  "video_milestone",
  "video_rewatched",
  "forwarded",
  "pack_completed",
  "no_view_alert",
  "reshare_affirmed",
  "reshare_new_identity",
  "priority_escalated",
] as const;
export type EventType = (typeof EVENT_TYPES)[number];

// ─── New enums (audit V1 build) ─────────────────────────────────────────
export const VIDEO_SOURCES = ["mux", "youtube"] as const;
export type VideoSource = (typeof VIDEO_SOURCES)[number];

export const PLAYLIST_VISIBILITIES = ["private", "internal", "public"] as const;
export type PlaylistVisibility = (typeof PLAYLIST_VISIBILITIES)[number];

export const VIEWER_PRIORITIES = ["normal", "high"] as const;
export type ViewerPriority = (typeof VIEWER_PRIORITIES)[number];

export const CAMPAIGN_PERSONAS = [
  "frylow_sales",
  "elevate_sales",
  "marketing_sales",
  "general",
] as const;
export type CampaignPersona = (typeof CAMPAIGN_PERSONAS)[number];

export const VIEWER_ROLES = [
  "engineer",
  "sales",
  "marketing",
  "operations",
  "leadership",
  "other",
] as const;
export type ViewerRole = (typeof VIEWER_ROLES)[number];

// Engagement-priority escalation thresholds (matched by the priority sweep job).
export const PRIORITY_ESCALATION_COMPLETION_THRESHOLD = 75; // percent
export const PRIORITY_ESCALATION_REVISIT_THRESHOLD = 2; // distinct opens

// ─── Email ──────────────────────────────────────────────────────────────
export const EMAIL_STATUSES = ["queued", "sent", "failed"] as const;
export type EmailStatus = (typeof EMAIL_STATUSES)[number];

export const EMAIL_TEMPLATES = [
  "demo_pack_invite",
  "forward_invite",
  "no_view_reminder",
  "engagement_alert",
  "user_invite",
  "password_reset",
  "quarterly_review_due",
  // Audit V1: viewer-facing thank-you on completion. Respects
  // issuances.suppress_analytics_in_email (defaults ON, so {{packSummary}}
  // is empty unless the sender explicitly opted in to sharing stats).
  "viewer_thanks",
] as const;
export type EmailTemplate = (typeof EMAIL_TEMPLATES)[number];

// ─── Milestones (video completion) ──────────────────────────────────────
export const VIDEO_MILESTONES = [25, 50, 75, 100] as const;
export type VideoMilestone = (typeof VIDEO_MILESTONES)[number];

// ─── Governance ─────────────────────────────────────────────────────────
export const REVIEW_PERIOD_DAYS = 90;
export const REVIEW_STALE_QUARTERS = 2;

export const ISSUANCE_DEFAULT_EXPIRY_DAYS = 30;
